import React from "react";
import { Volume2, VolumeX, Wind, Activity } from "lucide-react";

/**
 * AudioBreathControls — compact glass strip with toggles for:
 *  - ambient soundscape on/off
 *  - binaural delta on/off
 *  - volume slider
 *  - breathing pacer on/off
 *  - pacer pattern selector
 */
export default function AudioBreathControls({
  audio,
  breathingOn,
  setBreathingOn,
  pattern,
  setPattern,
}) {
  return (
    <div
      className="glass rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap"
      data-testid="audio-breath-controls"
    >
      <button
        type="button"
        onClick={() => audio.setEnabled(!audio.enabled)}
        className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-widest uppercase border transition-all flex items-center gap-2 ${
          audio.enabled
            ? "bg-[color:var(--accent-cyan-muted)] border-[color:var(--accent-cyan)] text-[color:var(--accent-cyan)]"
            : "border-[color:var(--hairline)] text-[color:var(--text-secondary)] hover:border-[color:rgba(15,26,46,0.20)]"
        }`}
        data-testid="audio-toggle-btn"
        title="Toggle ambient soundscape"
      >
        {audio.enabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
        Sound
      </button>

      <button
        type="button"
        onClick={() => audio.setBinaural(!audio.binaural)}
        disabled={!audio.enabled}
        className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-widest uppercase border transition-all flex items-center gap-2 disabled:opacity-40 ${
          audio.binaural
            ? "bg-[color:var(--accent-cyan-muted)] border-[color:var(--accent-cyan)] text-[color:var(--accent-cyan)]"
            : "border-[color:var(--hairline)] text-[color:var(--text-secondary)] hover:border-[color:rgba(15,26,46,0.20)]"
        }`}
        data-testid="binaural-toggle-btn"
        title="Toggle 4 Hz binaural delta layer"
      >
        <Activity size={14} />
        Binaural δ
      </button>

      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--text-secondary)]">
          Vol
        </span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={audio.volume}
          onChange={(e) => audio.setVolume(Number(e.target.value))}
          disabled={!audio.enabled}
          className="w-24 accent-[color:var(--accent-cyan)] disabled:opacity-40"
          data-testid="audio-volume-slider"
        />
      </div>

      <div className="w-px h-6 bg-[color:rgba(15,26,46,0.05)]" />

      <button
        type="button"
        onClick={() => setBreathingOn(!breathingOn)}
        className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-widest uppercase border transition-all flex items-center gap-2 ${
          breathingOn
            ? "bg-[color:var(--accent-cyan-muted)] border-[color:var(--accent-cyan)] text-[color:var(--accent-cyan)]"
            : "border-[color:var(--hairline)] text-[color:var(--text-secondary)] hover:border-[color:rgba(15,26,46,0.20)]"
        }`}
        data-testid="breath-toggle-btn"
        title="Toggle breathing pacer"
      >
        <Wind size={14} />
        Pacer
      </button>

      {breathingOn && (
        <select
          value={pattern}
          onChange={(e) => setPattern(e.target.value)}
          disabled={!breathingOn}
          className="bg-[color:var(--bg-soft)] border border-[color:var(--hairline)] rounded-full px-3 py-1 font-mono text-xs text-ink focus:outline-none focus:border-[color:var(--accent-cyan)] disabled:opacity-40"
          data-testid="breath-pattern-select"
        >
          <option value="4-4-6-2">4-4-6-2 (calming)</option>
          <option value="4-7-8">4-7-8 (sleep)</option>
          <option value="5-5">5-5 (resonance)</option>
          <option value="box">4-4-4-4 (box)</option>
        </select>
      )}
    </div>
  );
}
